import { BoardConst } from './rules_spec/constants.js';

function bit_length(n: number): number {
    if (n === 0) return 0;
    return 32 - Math.clz32(n >>> 0);
}

export class BoardState {
    board_x: number;
    board_y: number;
    N_max_bits: number;
    N_pieces: number;
    N_ally_pieces: number;

    ray_keys!: BoardConst[];
    N_rayBB!: Record<BoardConst, number>;
    N_max_diag_cells!: number;
    offset_rayBB!: Record<BoardConst, number>;
    bitboard_size!: number;

    rayBB32!: Uint32Array;
    rayRankBB_hi!: Uint32Array;
    rayRankBB_lo!: Uint32Array;

    pieceSq16!: Int16Array;
    board16!: Int16Array;

    board_xm1!: number;
    board_ym1!: number;
    board_ym3!: number;
    maxxym1!: number;

    _direction_cache!: Map<number, Record<BoardConst, number>>;

    _scan_pair_map!: Record<BoardConst, [BoardConst, BoardConst, (x: number, y: number, rank: number) => [number, number]]>;

    table_top!: Uint32Array;
    table_bottom!: Uint32Array;
    table_left!: Uint32Array;
    table_right!: Uint32Array;
    table_topleft!: Uint32Array;
    table_bottomright!: Uint32Array;
    table_topright!: Uint32Array;
    table_bottomleft!: Uint32Array;

    constructor(board_x: number = 36, board_y: number = 36, N_max_bits: number = 32, N_pieces: number = 804) {
        this.board_x = board_x;
        this.board_y = board_y;
        this.N_max_bits = N_max_bits;
        this.N_pieces = N_pieces;
        this.N_ally_pieces = Math.floor(N_pieces / 2);

        this._init_bitboard();
        this._init_ray_tables();
    }

    _init_bitboard() {
        this.ray_keys = [
            BoardConst.TOP, BoardConst.BOTTOM, BoardConst.LEFT, BoardConst.RIGHT, 
            BoardConst.TOP_LEFT, BoardConst.BOTTOM_RIGHT, BoardConst.TOP_RIGHT, BoardConst.BOTTOM_LEFT
        ];

        this.N_rayBB = {} as any;
        this.N_rayBB[BoardConst.TOP] = this.board_x;
        this.N_rayBB[BoardConst.BOTTOM] = this.board_x;
        this.N_rayBB[BoardConst.LEFT] = this.board_y;
        this.N_rayBB[BoardConst.RIGHT] = this.board_y;

        this.N_rayBB[BoardConst.TOP_LEFT] = this.board_x + this.board_y - 5;
        this.N_max_diag_cells = Math.min(this.board_x, this.board_y) - 2;
        this.N_rayBB[BoardConst.BOTTOM_RIGHT] = (this.N_max_diag_cells - this.N_max_bits) * 2 - 1;
        this.N_rayBB[BoardConst.TOP_RIGHT] = this.N_rayBB[BoardConst.TOP_LEFT];
        this.N_rayBB[BoardConst.BOTTOM_LEFT] = this.N_rayBB[BoardConst.BOTTOM_RIGHT];

        this.offset_rayBB = {} as any;
        let current_offset = 0;
        for (const ray_key of this.ray_keys) {
            this.offset_rayBB[ray_key] = current_offset;
            current_offset += this.N_rayBB[ray_key];
        }

        this.bitboard_size = current_offset;
        this.rayBB32 = new Uint32Array(this.bitboard_size);
        this.rayRankBB_hi = new Uint32Array(this.bitboard_size);
        this.rayRankBB_lo = new Uint32Array(this.bitboard_size);

        this.pieceSq16 = new Int16Array(this.N_pieces + 1);
        this.board16 = new Int16Array(this.board_x * this.board_y);

        this.board_xm1 = this.board_x - 1;
        this.board_ym1 = this.board_y - 1;
        this.board_ym3 = this.board_y - 3;
        this.maxxym1 = Math.max(this.board_x, this.board_y) - 1;

        this._direction_cache = new Map();

        this._scan_pair_map = {
            [BoardConst.TOP]:          [BoardConst.TOP,       BoardConst.BOTTOM,       this._scan_vertical_distance.bind(this)],
            [BoardConst.BOTTOM]:       [BoardConst.TOP,       BoardConst.BOTTOM,       this._scan_vertical_distance.bind(this)],
            [BoardConst.LEFT]:         [BoardConst.LEFT,      BoardConst.RIGHT,        this._scan_horizontal_distance.bind(this)],
            [BoardConst.RIGHT]:        [BoardConst.LEFT,      BoardConst.RIGHT,        this._scan_horizontal_distance.bind(this)],
            [BoardConst.TOP_LEFT]:     [BoardConst.TOP_LEFT,  BoardConst.BOTTOM_RIGHT, this._scan_backslash_distance.bind(this)],
            [BoardConst.BOTTOM_RIGHT]: [BoardConst.TOP_LEFT,  BoardConst.BOTTOM_RIGHT, this._scan_backslash_distance.bind(this)],
            [BoardConst.TOP_RIGHT]:    [BoardConst.TOP_RIGHT, BoardConst.BOTTOM_LEFT,  this._scan_slash_distance.bind(this)],
            [BoardConst.BOTTOM_LEFT]:  [BoardConst.TOP_RIGHT, BoardConst.BOTTOM_LEFT,  this._scan_slash_distance.bind(this)],
        } as any;
    }

    _init_ray_tables() {
        const bottom_start_y = this.board_y - this.N_max_bits - 1;

        this.table_top = new Uint32Array(this.board_y);
        this.table_bottom = new Uint32Array(this.board_y);

        for (let y = 0; y < this.board_y; y++) {
            if (0 < y && y <= this.N_max_bits) {
                this.table_top[y] = (1 << (y - 1));
            }
            if (bottom_start_y <= y && y < (this.board_y - 1)) {
                this.table_bottom[y] = (1 << (y - bottom_start_y));
            }
        }

        const right_start_x = this.board_x - this.N_max_bits - 1;
        this.table_left = new Uint32Array(this.board_x);
        this.table_right = new Uint32Array(this.board_x);

        for (let x = 0; x < this.board_x; x++) {
            if (0 < x && x <= this.N_max_bits) {
                this.table_left[x] = (1 << (x - 1));
            }
            if (right_start_x <= x && x < (this.board_x - 1)) {
                this.table_right[x] = (1 << (x - right_start_x));
            }
        }

        const mxy_len = Math.max(this.board_x, this.board_y);
        this.table_topleft = new Uint32Array(mxy_len);
        this.table_bottomright = new Uint32Array(mxy_len);
        const bottomright_start_mxy = bottom_start_y;

        for (let mxy = 0; mxy < mxy_len; mxy++) {
            if (0 < mxy && mxy <= this.N_max_bits) {
                this.table_topleft[mxy] = (1 << (mxy - 1));
            }
            if (bottomright_start_mxy <= mxy && mxy < (this.board_x - 1)) {
                this.table_bottomright[mxy] = (1 << (mxy - bottomright_start_mxy));
            }
        }

        this.table_topright = this.table_topleft;
        this.table_bottomleft = this.table_bottomright;
    }

    _update_ray_bitboard(target_bb: Uint32Array, x: number, y: number) {
        target_bb[this.offset_rayBB[BoardConst.TOP] + x] ^= this.table_top[y];
        target_bb[this.offset_rayBB[BoardConst.BOTTOM] + x] ^= this.table_bottom[y];
        target_bb[this.offset_rayBB[BoardConst.LEFT] + y] ^= this.table_left[x];
        target_bb[this.offset_rayBB[BoardConst.RIGHT] + y] ^= this.table_right[x];

        let topleft_idx = x + this.board_y - y - 3;
        if (0 <= topleft_idx && topleft_idx < this.N_rayBB[BoardConst.TOP_LEFT]) {
            target_bb[this.offset_rayBB[BoardConst.TOP_LEFT] + topleft_idx] ^= this.table_topleft[Math.min(x, y)];
        }
        
        let bottomright_idx = Math.floor(topleft_idx) - this.N_max_bits;
        if (0 <= bottomright_idx && bottomright_idx < this.N_rayBB[BoardConst.BOTTOM_RIGHT]) {
            target_bb[this.offset_rayBB[BoardConst.BOTTOM_RIGHT] + bottomright_idx] ^= this.table_bottomright[Math.max(x, y)];
        }

        let topright_idx = x + y - 2;
        if (0 <= topright_idx && topright_idx < this.N_rayBB[BoardConst.TOP_RIGHT]) {
            let dist_tr = Math.min(this.board_xm1 - x, y);
            target_bb[this.offset_rayBB[BoardConst.TOP_RIGHT] + topright_idx] ^= this.table_topleft[dist_tr];
        }

        let bottomleft_idx = Math.floor(topright_idx) - this.N_max_bits;
        if (0 <= bottomleft_idx && bottomleft_idx < this.N_rayBB[BoardConst.BOTTOM_LEFT]) {
            let dist_bl = Math.max(this.board_xm1 - x, y);
            target_bb[this.offset_rayBB[BoardConst.BOTTOM_LEFT] + bottomleft_idx] ^= this.table_bottomright[dist_bl];
        }
    }

    _toggle_piece_ray(x: number, y: number, rank: number) {
        this._update_ray_bitboard(this.rayBB32, x, y);
        if ((rank >> 1) & 1) {
            this._update_ray_bitboard(this.rayRankBB_hi, x, y);
        }
        if (rank & 1) {
            this._update_ray_bitboard(this.rayRankBB_lo, x, y);
        }
    }

    _put_piece(x: number, y: number, piece_instance_id: number, is_nari: boolean | null = null, rank: number | null = null) {
        if (rank === null || is_nari === null) {
            const packed_val = this.pieceSq16[piece_instance_id];
            if (rank === null) rank = packed_val >> 14;
            if (is_nari === null) is_nari = ((packed_val >> 12) & 1) > 0;
        }

        this._toggle_piece_ray(x, y, rank);

        const status_bits = is_nari ? 3 : 2;
        const packed_val = (rank << 14) | (status_bits << 12) | (y << 6) | x;
        this.pieceSq16[piece_instance_id] = packed_val;
        this.board16[y * this.board_x + x] = piece_instance_id;
        this._direction_cache.clear();
    }

    _remove_piece(piece_instance_id: number) {
        const packed_val = this.pieceSq16[piece_instance_id];
        const x = packed_val & 0x3f;
        const y = (packed_val >> 6) & 0x3f;
        const rank = packed_val >> 14;

        this.pieceSq16[piece_instance_id] = packed_val & ~(1 << 13);
        this._toggle_piece_ray(x, y, rank);
        this.board16[y * this.board_x + x] = 0;
        this._direction_cache.clear();
    }

    _get_bits(rank: number, idx: number): number {
        if (rank === 0) return this.rayBB32[idx];
        else if (rank === 1) return this.rayRankBB_hi[idx] | this.rayRankBB_lo[idx];
        else if (rank === 2) return this.rayRankBB_hi[idx];
        else return this.rayRankBB_hi[idx] & this.rayRankBB_lo[idx];
    }

    _scan_orthogonal_generic(pos: number, line_idx: number, limit: number, near_key: BoardConst, far_key: BoardConst, near_table: Uint32Array, far_table: Uint32Array, rank: number): [number, number] {
        const near_bits = this._get_bits(rank, this.offset_rayBB[near_key] + line_idx) >>> 0;
        const far_bits = this._get_bits(rank, this.offset_rayBB[far_key] + line_idx) >>> 0;

        const b_near = near_table[pos] >>> 0;
        const b_far = far_table[pos] >>> 0;

        const CROSS_OFFSET = 2;
        let dist_near = 0, dist_far = 0;

        if (pos === 0) {
            dist_near = 0;
        } else if (b_near) {
            const lower_bits = near_bits & (b_near - 1);
            dist_near = pos - bit_length(lower_bits);
        } else {
            if (b_far) {
                const lower_bits = far_bits & (b_far - 1);
                if (lower_bits) {
                    dist_near = bit_length(b_far) - bit_length(lower_bits);
                } else {
                    dist_near = pos - bit_length(near_bits);
                }
            } else {
                if (far_bits) {
                    dist_near = pos - bit_length(far_bits) - CROSS_OFFSET;
                } else {
                    dist_near = pos - bit_length(near_bits);
                }
            }
        }

        if (pos === limit) {
            dist_far = 0;
        } else if (b_far) {
            const upper_bits = far_bits & ~(b_far - 1 | b_far);
            if (upper_bits) {
                const blocker = upper_bits & -upper_bits;
                dist_far = bit_length(blocker) - bit_length(b_far);
            } else {
                dist_far = limit - pos;
            }
        } else {
            let upper_bits = 0;
            if (b_near) {
                upper_bits = near_bits & ~(b_near - 1 | b_near);
            } else {
                upper_bits = near_bits;
            }

            if (upper_bits) {
                const blocker = upper_bits & -upper_bits;
                dist_far = bit_length(blocker) - pos;
            } else {
                if (far_bits === 0) {
                    dist_far = limit - pos;
                } else {
                    const blocker = far_bits & -far_bits;
                    dist_far = bit_length(blocker) + CROSS_OFFSET - pos;
                }
            }
        }

        return [dist_near, dist_far];
    }

    _scan_vertical_distance(x: number, y: number, rank: number): [number, number] {
        return this._scan_orthogonal_generic(y, x, this.board_ym1, BoardConst.TOP, BoardConst.BOTTOM, this.table_top, this.table_bottom, rank);
    }

    _scan_horizontal_distance(x: number, y: number, rank: number): [number, number] {
        return this._scan_orthogonal_generic(x, y, this.board_xm1, BoardConst.LEFT, BoardConst.RIGHT, this.table_left, this.table_right, rank);
    }

    _scan_diagonal_generic(m_xy: number, M_xy: number, limit_dist: number, diag_idx: number, near_key: BoardConst, far_key: BoardConst, near_table: Uint32Array, far_table: Uint32Array, rank: number): [number, number] {
        if (!(0 <= diag_idx && diag_idx < this.N_rayBB[near_key])) {
            return [m_xy, limit_dist];
        }

        const near_bits = this._get_bits(rank, this.offset_rayBB[near_key] + diag_idx) >>> 0;
        let has_far = false;
        let far_bits = 0;
        const far_idx = Math.floor(diag_idx) - this.N_max_bits;
        if (diag_idx >= this.N_max_bits) {
            if (far_idx < this.N_rayBB[far_key]) {
                has_far = true;
                far_bits = this._get_bits(rank, this.offset_rayBB[far_key] + far_idx) >>> 0;
            }
        }

        const b_near = near_table[m_xy] >>> 0;
        const b_far = far_table[M_xy] >>> 0;

        const CROSS_OFFSET = 2;
        let dist_near = 0, dist_far = 0;

        if (m_xy === 0) {
            dist_near = 0;
        } else if (b_near) {
            const lower_bits = near_bits & (b_near - 1);
            dist_near = m_xy - bit_length(lower_bits);
        } else {
            if (b_far) {
                const lower_bits = far_bits & (b_far - 1);
                if (lower_bits) {
                    dist_near = bit_length(b_far) - bit_length(lower_bits);
                } else {
                    dist_near = m_xy - bit_length(near_bits);
                }
            } else {
                if (far_bits) {
                    dist_near = m_xy - bit_length(far_bits) - CROSS_OFFSET;
                } else {
                    dist_near = m_xy - bit_length(near_bits);
                }
            }
        }

        if (M_xy === this.maxxym1) {
            dist_far = 0;
        } else if (b_far && has_far) {
            const upper_bits = far_bits & ~(b_far - 1 | b_far);
            if (upper_bits) {
                const blocker = upper_bits & -upper_bits;
                dist_far = bit_length(blocker) - bit_length(b_far);
            } else {
                dist_far = limit_dist;
            }
        } else {
            let upper_bits = 0;
            if (b_near) {
                upper_bits = near_bits & ~(b_near - 1 | b_near);
            } else {
                upper_bits = near_bits;
            }
            if (upper_bits) {
                const blocker = upper_bits & -upper_bits;
                dist_far = bit_length(blocker) - m_xy;
            } else {
                if (far_bits === 0) {
                    dist_far = limit_dist;
                } else {
                    const blocker = far_bits & -far_bits;
                    dist_far = bit_length(blocker) + CROSS_OFFSET - M_xy;
                }
            }
        }

        return [dist_near, dist_far];
    }

    _scan_backslash_distance(x: number, y: number, rank: number): [number, number] {
        return this._scan_diagonal_generic(
            Math.min(x, y), Math.max(x, y), Math.min(this.board_xm1 - x, this.board_ym1 - y),
            x + this.board_ym3 - y,
            BoardConst.TOP_LEFT, BoardConst.BOTTOM_RIGHT,
            this.table_topleft, this.table_bottomright, rank
        );
    }

    _scan_slash_distance(x: number, y: number, rank: number): [number, number] {
        return this._scan_diagonal_generic(
            Math.min(this.board_xm1 - x, y), Math.max(this.board_xm1 - x, y), Math.min(x, this.board_ym1 - y),
            x + y - 2,
            BoardConst.TOP_RIGHT, BoardConst.BOTTOM_LEFT,
            this.table_topleft, this.table_bottomright, rank
        );
    }

    scan_direction(x: number, y: number, direction: BoardConst, rank: number = 0): number {
        const cache_key = (rank << 12) | (y << 6) | x;
        let key_cache = this._direction_cache.get(cache_key);
        if (key_cache) {
            const val = key_cache[direction];
            if (val !== undefined) return val;
        } else {
            key_cache = {} as Record<BoardConst, number>;
            this._direction_cache.set(cache_key, key_cache);
        }

        const [near_dir, far_dir, scan_func] = this._scan_pair_map[direction];
        const [dist_near, dist_far] = scan_func(x, y, rank);
        key_cache[near_dir] = dist_near;
        key_cache[far_dir] = dist_far;

        return key_cache[direction];
    }

    get_scan_distance(pid: number, direction: BoardConst): number {
        const packed = this.pieceSq16[pid];
        const x = packed & 0x3F;
        const y = (packed >> 6) & 0x3F;
        const rank = packed >> 14;
        return this.scan_direction(x, y, direction, rank);
    }

    _scan_skip3_diagonal_generic(m_xy: number, M_xy: number, diag_idx: number, near_key: BoardConst, far_key: BoardConst, near_table: Uint32Array, far_table: Uint32Array, top_diag: boolean, bottom_diag: boolean): [number, number] {
        const near_bits_i = this.rayBB32[this.offset_rayBB[near_key] + diag_idx] >>> 0;
        const b_near_i = near_table[m_xy] >>> 0;
        const b_far_i = far_table[M_xy] >>> 0;
        const far_idx = Math.floor(diag_idx) - this.N_max_bits;

        let dist_near = 0;
        let dist_far = 0;

        if (top_diag) {
            let skip_count = 3;
            if (m_xy >= 34) {
                const far_bits = this.rayBB32[this.offset_rayBB[far_key] + far_idx] >>> 0;
                if (M_xy === 35) {
                    if (far_bits & (1 << 31)) skip_count -= 1;
                }
                if (far_idx === 1) {
                    if (far_bits & (1 << 30)) skip_count -= 1;
                }
            }

            if (m_xy < 5) {
                dist_near = m_xy;
            } else {
                let lower_bits = near_bits_i & (b_near_i - 1);
                if (lower_bits && skip_count >= 3) lower_bits ^= (1 << (bit_length(lower_bits) - 1));
                if (lower_bits && skip_count >= 2) lower_bits ^= (1 << (bit_length(lower_bits) - 1));
                if (lower_bits && skip_count >= 1) lower_bits ^= (1 << (bit_length(lower_bits) - 1));
                
                dist_near = m_xy - bit_length(lower_bits);
            }
        }

        if (bottom_diag) {
            if (M_xy > 30) {
                dist_far = 35 - M_xy;
            } else {
                let skip_count = 3;
                let upper_bits = near_bits_i & ~(b_near_i - 1 | b_near_i);
                
                if (upper_bits && skip_count >= 3) { upper_bits &= upper_bits - 1; skip_count -= 1; }
                if (upper_bits && skip_count >= 2) { upper_bits &= upper_bits - 1; skip_count -= 1; }
                if (upper_bits && skip_count >= 1) { upper_bits &= upper_bits - 1; skip_count -= 1; }

                if (upper_bits) {
                    const blocker = upper_bits & -upper_bits;
                    dist_far = bit_length(blocker) - bit_length(b_near_i);
                } else if (0 <= far_idx && far_idx < 3) {
                    const far_bits = this.rayBB32[this.offset_rayBB[far_key] + far_idx] >>> 0;
                    if (far_idx === 1 && (far_bits & (1 << 30))) {
                        if (skip_count === 0) dist_far = 33 - M_xy;
                        skip_count -= 1;
                    }
                    if (skip_count >= 0 && (far_bits & (1 << 31))) {
                        if (skip_count === 0) dist_far = 34 - M_xy;
                        else dist_far = 35 - M_xy;
                    }
                }
            }
        }
        return [dist_near, dist_far];
    }

    _scan_skip3_backslash_distance(x: number, y: number, top_diag: boolean, bottom_diag: boolean): [number, number] {
        return this._scan_skip3_diagonal_generic(
            Math.min(x, y), Math.max(x, y), x + this.board_ym3 - y,
            BoardConst.TOP_LEFT, BoardConst.BOTTOM_RIGHT,
            this.table_topleft, this.table_bottomright, top_diag, bottom_diag
        );
    }

    _scan_skip3_slash_distance(x: number, y: number, top_diag: boolean, bottom_diag: boolean): [number, number] {
        return this._scan_skip3_diagonal_generic(
            Math.min(this.board_xm1 - x, y), Math.max(this.board_xm1 - x, y), x + y - 2,
            BoardConst.TOP_RIGHT, BoardConst.BOTTOM_LEFT,
            this.table_topleft, this.table_bottomright, top_diag, bottom_diag
        );
    }

    _scan_skip3_orthogonal_generic(pos: number, line_idx: number, limit: number, near_key: BoardConst, far_key: BoardConst, near_table: Uint32Array, far_table: Uint32Array): [number, number] {
        const near_bits_i = this.rayBB32[this.offset_rayBB[near_key] + line_idx] >>> 0;
        const far_bits_i = this.rayBB32[this.offset_rayBB[far_key] + line_idx] >>> 0;
        const b_near_i = near_table[pos] >>> 0;
        const b_far_i = far_table[pos] >>> 0;

        let dist_near = 0, dist_far = 0;

        if (pos < 5) {
            dist_near = pos;
        } else {
            let skip_count = 3;
            if (pos > 33) {
                if (far_bits_i & (1 << 30)) skip_count -= 1;
                if (pos > 34) {
                    if (far_bits_i & (1 << 31)) skip_count -= 1;
                }
            }
            let lower_bits = near_bits_i & (b_near_i - 1);
            if (lower_bits && skip_count >= 3) lower_bits ^= (1 << (bit_length(lower_bits) - 1));
            if (lower_bits && skip_count >= 2) lower_bits ^= (1 << (bit_length(lower_bits) - 1));
            if (lower_bits && skip_count >= 1) lower_bits ^= (1 << (bit_length(lower_bits) - 1));
            dist_near = pos - bit_length(lower_bits);
        }

        if (pos > 30) {
            dist_far = 35 - pos;
        } else {
            let skip_count = 3;
            if (pos < 2) {
                if (near_bits_i & 2) skip_count -= 1;
                if (pos < 1) {
                    if (near_bits_i & 1) skip_count -= 1;
                }
            }
            let upper_bits = far_bits_i & ~(b_far_i - 1 | b_far_i);
            if (upper_bits && skip_count >= 3) upper_bits &= upper_bits - 1;
            if (upper_bits && skip_count >= 2) upper_bits &= upper_bits - 1;
            if (upper_bits && skip_count >= 1) upper_bits &= upper_bits - 1;

            if (upper_bits) {
                const blocker = upper_bits & -upper_bits;
                dist_far = bit_length(blocker) - bit_length(b_far_i);
            } else {
                dist_far = limit - pos;
            }
        }
        return [dist_near, dist_far];
    }

    _scan_skip3_orthogonal_distance(x: number, y: number, rank: number): [number, number, number, number] {
        const [dist_top, dist_bottom] = this._scan_skip3_orthogonal_generic(
            y, x, this.board_ym1, BoardConst.TOP, BoardConst.BOTTOM, this.table_top, this.table_bottom
        );
        const [dist_left, dist_right] = this._scan_skip3_orthogonal_generic(
            x, y, this.board_xm1, BoardConst.LEFT, BoardConst.RIGHT, this.table_left, this.table_right
        );
        return [dist_top, dist_left, dist_bottom, dist_right];
    }
}
